import { create } from 'zustand';

import SocketService from '@src/services/SocketService';

import { IPeer, IPeerInfo, TPeerMap } from '@src/types';

interface IPeerStore {
  // State
  peers: TPeerMap;
  isHostUser: boolean;
  raisedHandsQueue: string[];
  viewersCount: number;
  shouldRefetchPeerPermissions: boolean;
  avatarUrl: string | null;

  // Peer Management Actions
  initPeers: (peers: TPeerMap) => void;
  appendPeer: (id: string, peer: IPeer) => void;
  deletePeer: (id: string) => void;
  updatePeerData: (peerId: string, options: Partial<IPeer>) => void;
  updatePeerInfo: (peerId: string, options: Partial<IPeerInfo>) => void;

  // Hand Raise Actions
  raiseHand: (peerId: string) => void;
  lowerHand: (peerId: string) => void;

  // Viewer Count Actions
  setViewersCount: (count: number) => void;

  // Permission Refetch Action
  setShouldRefetchPeerPermissions: (shouldRefetch: boolean) => void; // New action

  // Avatar Action
  setAvatarUrl: (url: string) => void;

  // Getters
  getMyPeerInfo: () => IPeerInfo | null;
  getPeerInfoById: (peerId: string) => IPeerInfo | null;
  getPeerColor: (peerId: string) => number | null;

  // Reset
  resetStates: () => void;
}

const defaultState: Pick<
  IPeerStore,
  'peers' | 'isHostUser' | 'raisedHandsQueue' | 'viewersCount' | 'shouldRefetchPeerPermissions' | 'avatarUrl'
> = {
  peers: new Map(),
  isHostUser: false,
  raisedHandsQueue: [],
  viewersCount: 0,
  shouldRefetchPeerPermissions: false,
  avatarUrl: null,
};

export const usePeersStore = create<IPeerStore>((set, get) => ({
  ...defaultState,

  initPeers: (peers) =>
    set((state) => {
      return { ...state, peers };
    }),

  appendPeer: (id, peer) =>
    set((state) => {
      const updatedPeers = new Map(state.peers);
      updatedPeers.set(id, { ...updatedPeers.get(id), ...peer });
      return { ...state, peers: updatedPeers };
    }),

  deletePeer: (id) =>
    set((state) => {
      const updatedPeers = new Map(state.peers);
      updatedPeers.delete(id);
      return { ...state, peers: updatedPeers };
    }),

  updatePeerData: (peerId, options) =>
    set((state) => {
      const updatedPeers = new Map(state.peers);
      let currentPeer = updatedPeers.get(peerId);
      if (currentPeer) {
        currentPeer = { ...currentPeer, ...options };
        updatedPeers.set(peerId, currentPeer);
      }

      return { ...state, peers: updatedPeers };
    }),

  updatePeerInfo: (peerId, options) =>
    set((state) => {
      const updatedPeers = new Map(state.peers);
      let currentPeer = updatedPeers.get(peerId);
      if (currentPeer) {
        currentPeer = { ...currentPeer, peer_info: { ...currentPeer.peer_info, ...options } };
        updatedPeers.set(peerId, currentPeer);
      }
      return { ...state, peers: updatedPeers };
    }),

  raiseHand: (peerId) =>
    set((state) => {
      if (!state.raisedHandsQueue.includes(peerId)) {
        const updatedPeers = new Map(state.peers);
        const peer = updatedPeers.get(peerId);

        if (peer) {
          updatedPeers.set(peerId, {
            ...peer,
            peer_info: { ...peer.peer_info, peer_hand: true },
          });
        }

        return {
          ...state,
          raisedHandsQueue: [...state.raisedHandsQueue, peerId],
          peers: updatedPeers,
        };
      }
      return state;
    }),

  lowerHand: (peerId) =>
    set((state) => {
      const updatedPeers = new Map(state.peers);
      const peer = updatedPeers.get(peerId);

      if (peer) {
        updatedPeers.set(peerId, {
          ...peer,
          peer_info: { ...peer.peer_info, peer_hand: false },
        });
      }

      return {
        ...state,
        raisedHandsQueue: state.raisedHandsQueue.filter((id) => id !== peerId),
        peers: updatedPeers,
      };
    }),

  setViewersCount: (count) => set({ viewersCount: count }),

  getMyPeerInfo: () => {
    const myPeer = get().peers.get(SocketService.getSocketId() ?? '') ?? null;
    return myPeer?.peer_info ?? null;
  },

  getPeerInfoById: (peerId) => get().peers.get(peerId)?.peer_info ?? null,

  getPeerColor: (peerId) => {
    const peer = get().peers.get(peerId);
    const peerColor = peer?.peer_info?.hue_color;

    if (peerColor == null) return null;

    return peerColor;
  },

  setShouldRefetchPeerPermissions: (shouldRefetchPeerPermissions) => set({ shouldRefetchPeerPermissions }),

  setAvatarUrl: (avatarUrl) => set({ avatarUrl }),

  resetStates: () => set({ ...defaultState }),
}));
